import { getSessionData } from './cookies';

export interface ActivityData {
    sessionId: string;
    action: string;
    page: string;
    data?: Record<string, any>;
    timestamp: Date;
    ipAddress?: string;
    userAgent?: string;
}

class Analytics {
    private activities: ActivityData[] = [];
    private useLocalStorage: boolean;

    constructor() {
        // Use localStorage for analytics in production when backend is not available
        this.useLocalStorage = process.env.REACT_APP_USE_LOCAL_STORAGE === 'true' || !process.env.REACT_APP_API_BASE_URL;
    }

    track(action: string, page: string, data?: Record<string, any>): void {
        const sessionData = getSessionData();
        if (!sessionData) return;

        const activity: ActivityData = {
            sessionId: sessionData.sessionId,
            action,
            page,
            data,
            timestamp: new Date(),
            // Note: IP address will be captured by the backend from request headers
            // User agent is captured here for completeness
            userAgent: navigator.userAgent
        };

        this.activities.push(activity);

        // Send to backend
        this.sendToBackend(activity);
    }    private async sendToBackend(activity: ActivityData): Promise<void> {
        // Skip backend requests if using localStorage mode
        if (this.useLocalStorage) {
            console.log('📊 Analytics (localStorage mode):', activity.action, activity.page, activity.data);
            return;
        }

        try {
            await fetch('/api/analytics', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(activity),
            });
        } catch (error) {
            console.error('Failed to send analytics data:', error);
        }
    }

    trackPageView(page: string): void {
        this.track('page_view', page);
    }

    trackFormStart(formName: string): void {
        this.track('form_start', formName);
    }

    trackFormComplete(formName: string, data?: Record<string, any>): void {
        this.track('form_complete', formName, data);
    }

    trackFormAbandonment(formName: string, step: string): void {
        this.track('form_abandon', formName, { step });
    }

    trackButtonClick(buttonName: string, page: string): void {
        this.track('button_click', page, { buttonName });
    }

    trackBookingStart(): void {
        this.track('booking_start', 'booking');
    }

    trackBookingComplete(bookingId: string): void {
        this.track('booking_complete', 'booking', { bookingId });
    }

    trackBookingAbandonment(step: string): void {
        this.track('booking_abandon', 'booking', { step });
    }

    trackError(errorType: string, errorMessage: string, data?: Record<string, any>): void {
        this.track('error', errorType, { errorMessage, ...data });
    }

    trackComponentClick(componentId: string, page: string, data?: Record<string, any>): void {
        this.track('component_click', page, { componentId, ...data });
    }

    // Enhanced price estimation tracking
    trackPriceEstimationStart(data?: Record<string, any>): void {
        this.track('price_estimation_start', 'pricing', data);
    }

    trackPriceEstimationStep(step: string, data?: Record<string, any>): void {
        this.track('price_estimation_step', 'pricing', { step, ...data });
    }

    trackPriceEstimationComplete(estimatedPrice: number, data?: Record<string, any>): void {
        this.track('price_estimated', 'pricing', { estimatedPrice, ...data });
    }

    trackPriceCalculatorUsage(action: string, data?: Record<string, any>): void {
        this.track('calculate_price', 'calculator', { action, ...data });
    }

    trackQuoteRequest(data?: Record<string, any>): void {
        this.track('get_quote', 'quote', data);
    }

    trackPricingPageView(section?: string): void {
        this.track('pricing_viewed', 'pricing', { section });
    }

    trackEstimateRequest(data?: Record<string, any>): void {
        this.track('estimate_requested', 'estimate', data);
    }

    // Enhanced pain point tracking
    trackPainPointCapture(painPoint: string, source: string, data?: Record<string, any>): void {
        this.track('pain_point_captured', source, { painPoint, ...data });
    }

    trackPainPointUpdate(oldPainPoint: string, newPainPoint: string, data?: Record<string, any>): void {
        this.track('pain_point_updated', 'profile', { oldPainPoint, newPainPoint, ...data });
    }

    trackSelectionChange(componentId: string, page: string, value: string | number | boolean): void {
        this.track('selection_change', page, { componentId, value });
    }

    trackToggle(componentId: string, page: string, isEnabled: boolean): void {
        this.track('toggle', page, { componentId, isEnabled });
    }

    trackDropdownChange(dropdownId: string, page: string, selectedValue: string | number): void {
        this.track('dropdown_change', page, { dropdownId, selectedValue });
    }

    trackInputChange(inputId: string, page: string, value: string): void {
        this.track('input_change', page, { inputId, value: typeof value === 'string' ? value.substring(0, 10) + (value.length > 10 ? '...' : '') : value });
    }

    trackNavigation(from: string, to: string): void {
        this.track('navigation', 'app', { from, to });
    }

    trackModalOpen(modalId: string, page: string): void {
        this.track('modal_open', page, { modalId });
    }

    trackModalClose(modalId: string, page: string): void {
        this.track('modal_close', page, { modalId });
    }

    trackCustomerConversion(conversionType: 'consultation_booked' | 'not_ready' | 'page_abandoned', page: string, data?: Record<string, any>): void {
        this.track('customer_conversion', page, { conversionType, ...data });
    }

    trackPageAbandonment(page: string, data?: Record<string, any>): void {
        // Use sendBeacon for reliable tracking on page unload
        const activity: ActivityData = {
            sessionId: getSessionData()?.sessionId || 'unknown',
            action: 'page_abandonment',
            page,
            data,
            timestamp: new Date(),
            userAgent: navigator.userAgent
        };

        if (navigator.sendBeacon) {
            navigator.sendBeacon('/api/analytics', JSON.stringify(activity));
        } else {
            // Fallback - this might not work reliably on page unload
            this.sendToBackend(activity);
        }
    }

    getActivities(): ActivityData[] {
        return this.activities;
    }
}

export const analytics = new Analytics();
